import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Topbar from "../Layouts/Topbar";
import Header from "../Layouts/Header";
import Footer from "../Layouts/Footer";

import { numberFormatter } from "../lib/utils";
import { API } from "../constants";

import useCompanyStore from "../hooks/useCompanyStore";
import useAuthStore from "../hooks/useAuthStore";
import useCartStore from "../hooks/useCartStore";

const FoodMenu = () => {
  const navigate = useNavigate();

  const { companyId } = useCompanyStore();

  const { user } = useAuthStore();

  const { setCartItemCount } = useCartStore();



  const loggedInUser =
    user?.user ||
    user ||
    null;

  const customerId =
    loggedInUser?._id ||
    loggedInUser?.id ||
    null;

  const customerName = [
    loggedInUser?.firstName,
    loggedInUser?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const customerPhone =
    loggedInUser?.phoneNumber ||
    "";

  const customerEmail =
    loggedInUser?.email ||
    "";



  const [
    foodCategories,
    setFoodCategories,
  ] = useState([]);

  const [
    activeFoodCategory,
    setActiveFoodCategory,
  ] = useState("");

  const [
    foodItems,
    setFoodItems,
  ] = useState([]);

  const [
    loadingCategories,
    setLoadingCategories,
  ] = useState(false);

  const [
    loadingFoodItems,
    setLoadingFoodItems,
  ] = useState(false);

  const [
    searchText,
    setSearchText,
  ] = useState("");



  const [
    cartLoading,
    setCartLoading,
  ] = useState(false);

  const [
    addingFoodId,
    setAddingFoodId,
  ] = useState("");

  
  const [
    modifierModalProduct,
    setModifierModalProduct,
  ] = useState(null);

  const [
    selectedModifierId,
    setSelectedModifierId,
  ] = useState("");

 

  const generateProductArray = (
    products = [],
  ) => {
    if (!Array.isArray(products)) {
      return [];
    }

    return products.map(
      (item) => ({
        id:
          item._id ||
          item.id,

        categoryId:
          item.categoryId,

        subCategoryId:
          item.subCategoryId,

        name:
          item.productName ||
          item.name ||
          "",

        description:
          item.productDescription ||
          item.description ||
          "",

        price: Number(
          item.salePriceExcludingTax ||
            item.salePrice ||
            item.price ||
            0,
        ),

        productTax:
          Number(
            item.taxRate ||
              0,
          ),

        image:
          item.productImage ||
          item.image ||
          "",

        allergens:
          item.allergens ||
          "",

        calories:
          item.calories ||
          "",

        weight:
          item.weight ||
          "",

        modifiers:
          Array.isArray(
            item.modifiers,
          )
            ? item.modifiers
            : [],

        rawProduct:
          item,
      }),
    );
  };



  const fetchFoodCategories =
    async () => {
      if (!companyId) {
        return;
      }

      try {
        setLoadingCategories(
          true,
        );

        const response =
          await fetch(
            `${API.LIST_FOOD_CATEGORIES}/${companyId}`,
            {
              headers: {
                Accept:
                  "application/json",
              },
            },
          );

        const result =
          await response.json();

        if (
          !response.ok ||
          result?.status ===
            false
        ) {
          console.error(
            "Unable to fetch food categories:",
            result,
          );

          setFoodCategories(
            [],
          );

          setActiveFoodCategory(
            "",
          );

          return;
        }

        const categories =
          Array.isArray(
            result?.data,
          )
            ? result.data
            : [];

        setFoodCategories(
          categories,
        );

        if (
          categories.length > 0
        ) {
          setActiveFoodCategory(
            (
              previousCategory,
            ) => {
              const categoryExists =
                categories.some(
                  (
                    category,
                  ) =>
                    String(
                      category._id ||
                        category.id,
                    ) ===
                    String(
                      previousCategory,
                    ),
                );

              if (
                categoryExists
              ) {
                return previousCategory;
              }

              return (
                categories[0]
                  ._id ||
                categories[0]
                  .id
              );
            },
          );
        } else {
          setActiveFoodCategory(
            "",
          );
        }
      } catch (error) {
        console.error(
          "Food category error:",
          error,
        );

        setFoodCategories(
          [],
        );

        setActiveFoodCategory(
          "",
        );
      } finally {
        setLoadingCategories(
          false,
        );
      }
    };

 

  const fetchProductByCategoryId =
    async (
      categoryId,
    ) => {
      if (
        !categoryId ||
        !companyId
      ) {
        setFoodItems([]);

        return;
      }

      try {
        setLoadingFoodItems(
          true,
        );

        const response =
          await fetch(
            `${API.LIST_PRODUCTBY_CATEGORYID}/${categoryId}/${companyId}`,
            {
              headers: {
                Accept:
                  "application/json",
              },
            },
          );

        const result =
          await response.json();

        if (
          !response.ok ||
          result?.status ===
            false
        ) {
          console.error(
            "Unable to fetch food products:",
            result,
          );

          setFoodItems([]);

          return;
        }

        const products =
          generateProductArray(
            result?.data ||
              [],
          );

        setFoodItems(
          products,
        );
      } catch (error) {
        console.error(
          "Food product error:",
          error,
        );

        setFoodItems([]);
      } finally {
        setLoadingFoodItems(
          false,
        );
      }
    };



  useEffect(() => {
    fetchFoodCategories();
  }, [companyId]);


  useEffect(() => {
    if (
      activeFoodCategory
    ) {
      fetchProductByCategoryId(
        activeFoodCategory,
      );
    } else {
      setFoodItems([]);
    }

    setSearchText("");
  }, [
    activeFoodCategory,
    companyId,
  ]);



  const filteredFoodItems =
    useMemo(() => {
      const search =
        searchText
          .trim()
          .toLowerCase();

      if (!search) {
        return foodItems;
      }

      return foodItems.filter(
        (item) => {
          const searchableText =
            [
              item.name,
              item.description,
              item.allergens,
              item.weight,
              item.calories,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();

          return searchableText.includes(
            search,
          );
        },
      );
    }, [
      foodItems,
      searchText,
    ]);



  const activeCategoryName =
    useMemo(() => {
      const category =
        foodCategories.find(
          (item) =>
            String(
              item._id ||
                item.id,
            ) ===
            String(
              activeFoodCategory,
            ),
        );

      return (
        category?.categoryName ||
        category?.name ||
        "Food Menu"
      );
    }, [
      foodCategories,
      activeFoodCategory,
    ]);



  const getProductImage = (
    item,
  ) => {
    if (!item?.image) {
      return "../assets/images/no-image.jpg";
    }

    if (
      String(
        item.image,
      ).startsWith(
        "http://",
      ) ||
      String(
        item.image,
      ).startsWith(
        "https://",
      )
    ) {
      return item.image;
    }

    return `${API.IMAGE_URL}/uploads/product/${item.image}`;
  };


  const extractCart = (
    result,
  ) => {
    return (
      result?.data?.cart ||
      result?.cart ||
      result?.data ||
      null
    );
  };



  const updateLocalCart = (
    result,
  ) => {
    const cart =
      extractCart(result);

    if (
      cart &&
      cart._id
    ) {
      const items =
        Array.isArray(
          cart.items,
        )
          ? cart.items
          : [];

      setCartItemCount(
        items.length,
      );

      return cart;
    }

    return null;
  };


  const addToCartApi =
    async (items) => {
   

      if (!customerId) {
        navigate("/login");

        return null;
      }

      if (!companyId) {
        console.error(
          "Company ID missing",
        );

        return null;
      }

      if (
        !Array.isArray(
          items,
        ) ||
        items.length === 0
      ) {
        return null;
      }

      try {
        setCartLoading(
          true,
        );

        const payload = {
          companyId:
            String(
              companyId,
            ),

          customerId:
            String(
              customerId,
            ),

          customerName,

          customerPhone,

          customerEmail,

          items,
        };

        const response =
          await fetch(
            API.ADD_TO_CART,
            {
              method:
                "POST",

              credentials:
                "include",

              headers: {
                Accept:
                  "application/json",

                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload,
                ),
            },
          );

        const result =
          await response
            .json()
            .catch(
              () => null,
            );

        if (
          !response.ok ||
          result?.status ===
            false ||
          result?.status ===
            0
        ) {
          console.error(
            "Add to cart failed:",
            result,
          );

          window.alert(
            result?.message ||
              "Unable to add item to cart.",
          );

          return null;
        }

        return updateLocalCart(
          result,
        );
      } catch (error) {
        console.error(
          "Add to cart error:",
          error,
        );

        window.alert(
          "Something went wrong while adding item to cart.",
        );

        return null;
      } finally {
        setCartLoading(
          false,
        );
      }
    };



  const addFoodItem =
    async (item) => {
      if (!customerId) {
        navigate("/login");

        return;
      }

      if (!item) {
        return;
      }

      const hasModifiers =
        Array.isArray(
          item.modifiers,
        ) &&
        item.modifiers
          .length > 0;

  

      if (hasModifiers) {
        setModifierModalProduct(
          item,
        );

        setSelectedModifierId(
          item.modifiers[0]
            ?._id ||
            item.modifiers[0]
              ?.id ||
            "",
        );

        return;
      }

      try {
        setAddingFoodId(
          item.id,
        );

   

        const quantity = 1;

        const unitPrice =
          Number(
            item.price ||
              0,
          );

        const subtotal =
          Number(
            (
              unitPrice *
              quantity
            ).toFixed(2),
          );

        const taxRate =
          Number(
            item.productTax ||
              0,
          );

        const taxAmount =
          Number(
            (
              subtotal *
              (taxRate /
                100)
            ).toFixed(2),
          );

        const totalAmount =
          Number(
            (
              subtotal +
              taxAmount
            ).toFixed(2),
          );

        const cart =
          await addToCartApi([
            {
              itemId:
                item.id,

              itemType:
                "food",

              categoryId:
                item.categoryId ||
                null,

              itemName:
                item.name ||
                "Product",

              image:
                item.image,

              quantity: 1,

              unitPrice,

              subtotal,

              taxRate,

              taxAmount,

              discountAmount:
                0,

              discountReasonId:
                null,

              discountReason:
                null,

              discountMethod:
                null,

              discountValue:
                0,

              discountPercentage:
                0,

              options: [],

              totalAmount,

              notes: "",
            },
          ]);

        if (cart) {
          console.log(
            "Food item added:",
            item.name,
          );
        }
      } finally {
        setAddingFoodId(
          "",
        );
      }
    };

 

  const closeModifierModal =
    () => {
      setModifierModalProduct(
        null,
      );

      setSelectedModifierId(
        "",
      );
    };


  const handleConfirmModifier =
    async () => {
      if (
        !modifierModalProduct
      ) {
        return;
      }

      if (!customerId) {
        navigate("/login");

        return;
      }

      const selectedModifier =
        modifierModalProduct.modifiers.find(
          (modifier) =>
            String(
              modifier._id ||
                modifier.id,
            ) ===
            String(
              selectedModifierId,
            ),
        );

      if (!selectedModifier) {
        return;
      }

      const modifierId =
        selectedModifier._id ||
        selectedModifier.id;

      const modifierPrice =
        Number(
          selectedModifier.price ||
            selectedModifier.modifierPrice ||
            0,
        );

      const basePrice =
        Number(
          modifierModalProduct.price ||
            0,
        );

      const unitPrice =
        Number(
          (
            basePrice +
            modifierPrice
          ).toFixed(2),
        );

     

      const quantity = 1;

      const subtotal =
        Number(
          (
            unitPrice *
            quantity
          ).toFixed(2),
        );

      const taxRate =
        Number(
          modifierModalProduct.productTax ||
            0,
        );

      const taxAmount =
        Number(
          (
            subtotal *
            (taxRate / 100)
          ).toFixed(2),
        );

      const totalAmount =
        Number(
          (
            subtotal +
            taxAmount
          ).toFixed(2),
        );

      const cart =
        await addToCartApi([
          {
            itemId:
              modifierModalProduct.id,

            itemType:
              "food",

            categoryId:
              modifierModalProduct.categoryId ||
              null,

            itemName:
              modifierModalProduct.name ||
              "Product",

            image:
              modifierModalProduct.image,

            quantity: 1,

            unitPrice,

            subtotal,

            taxRate,

            taxAmount,

            discountAmount:
              0,

            discountReasonId:
              null,

            discountReason:
              null,

            discountMethod:
              null,

            discountValue:
              0,

            discountPercentage:
              0,

            options: [
              {
                id: String(
                  modifierId,
                ),

                modifierId:
                  String(
                    modifierId,
                  ),

                name:
                  selectedModifier.modifierName ||
                  selectedModifier.name ||
                  "Modifier",

                modifierName:
                  selectedModifier.modifierName ||
                  selectedModifier.name ||
                  "Modifier",

                price:
                  modifierPrice,

                qty: 1,
              },
            ],

            totalAmount,

            notes: "",
          },
        ]);

      if (cart) {
        closeModifierModal();
      }
    };



  return (
    <div className="food-menu-main-page">

      <Topbar />

      <Header />



      <section className="food-menu-hero">

        <div className="container">

          <div className="food-menu-hero-content">

            <span className="food-menu-subtitle">
              Food & Beverages
            </span>

            <h1>
              Explore Our Food Menu
            </h1>

            <p>
              Choose from our
              delicious selection of
              food, snacks and
              beverages.
            </p>

          </div>

        </div>

      </section>



      <section className="food-menu-section">

        <div className="container">

          

          <div className="food-menu-header">

            <div>

              <span className="food-menu-small-title">
                Our Menu
              </span>

              <h2>
                {
                  activeCategoryName
                }
              </h2>

            </div>

            <div className="food-menu-search">

              <i className="fa-solid fa-magnifying-glass" />

              <input
                type="text"
                value={
                  searchText
                }
                placeholder="Search food..."
                onChange={(
                  event,
                ) =>
                  setSearchText(
                    event.target
                      .value,
                  )
                }
              />

              {searchText && (
                <button
                  type="button"
                  className="food-search-clear"
                  onClick={() =>
                    setSearchText(
                      "",
                    )
                  }
                >
                  ×
                </button>
              )}

            </div>

          </div>

 

          <div className="food-menu-categories">

            {loadingCategories ? (

              <div className="food-category-loading">
                Loading categories...
              </div>

            ) : foodCategories.length ===
              0 ? (

              <div className="food-category-empty">
                No food categories
                found.
              </div>

            ) : (

              foodCategories.map(
                (
                  category,
                ) => {
                  const categoryId =
                    category._id ||
                    category.id;

                  const categoryName =
                    category.categoryName ||
                    category.name ||
                    "Category";

                  const isActive =
                    String(
                      activeFoodCategory,
                    ) ===
                    String(
                      categoryId,
                    );

                  return (
                    <button
                      type="button"
                      key={
                        categoryId
                      }
                      className={
                        isActive
                          ? "food-menu-category-btn active"
                          : "food-menu-category-btn"
                      }
                      onClick={() =>
                        setActiveFoodCategory(
                          categoryId,
                        )
                      }
                    >
                      {
                        categoryName
                      }
                    </button>
                  );
                },
              )

            )}

          </div>



          <div className="food-menu-products">

            {loadingFoodItems ? (

              <div className="food-menu-message">

                <i className="fa-solid fa-spinner fa-spin" />

                <h3>
                  Loading food
                  items...
                </h3>

              </div>

            ) : filteredFoodItems.length ===
              0 ? (

              <div className="food-menu-message">

                <i className="fa-solid fa-utensils" />

                <h3>
                  {searchText
                    ? "No matching food found"
                    : "No food items found"}
                </h3>

                <p>
                  {searchText
                    ? `No food matches "${searchText}".`
                    : "No food items are currently available in this category."}
                </p>

              </div>

            ) : (

              <div className="food-menu-grid">

                {filteredFoodItems.map(
                  (item) => {
                    const hasModifiers =
                      Array.isArray(
                        item.modifiers,
                      ) &&
                      item.modifiers
                        .length > 0;

                    const isAdding =
                      String(
                        addingFoodId,
                      ) ===
                      String(
                        item.id,
                      );

                    return (
                      <div
                        className="food-menu-card"
                        key={
                          item.id
                        }
                      >

           

                        <div className="food-menu-image">

                          <img
                            src={getProductImage(
                              item,
                            )}
                            alt={
                              item.name
                            }
                            onError={(
                              event,
                            ) => {
                              event.currentTarget.src =
                                "../assets/images/no-image.jpg";
                            }}
                          />

                          {hasModifiers && (
                            <span className="food-customizable-badge">
                              Customizable
                            </span>
                          )}

                        </div>

            

                        <div className="food-menu-card-body">

                          <div className="food-menu-name-price">

                            <h3>
                              {
                                item.name
                              }
                            </h3>

                            <strong>
                              {numberFormatter.format(
                                item.price,
                              )}
                            </strong>

                          </div>

                          {item.description && (
                            <p className="food-menu-description">
                              {
                                item.description
                              }
                            </p>
                          )}

                

                          {(item.allergens ||
                            item.weight ||
                            item.calories) && (

                            <div className="food-menu-meta">

                              {item.weight && (
                                <span>

                                  <i className="fa-solid fa-weight-scale" />

                                  {
                                    item.weight
                                  }
                                  g

                                </span>
                              )}

                              {item.calories && (
                                <span>

                                  <i className="fa-solid fa-fire" />

                                  {
                                    item.calories
                                  }
                                  kcal

                                </span>
                              )}

                            </div>

                          )}

                  

                          {item.allergens && (

                            <div className="food-menu-allergens">

                              <strong>
                                Allergens:
                              </strong>

                              <span>
                                {
                                  item.allergens
                                }
                              </span>

                            </div>

                          )}


                          {hasModifiers && (

                            <div className="food-menu-customization">

                              <i className="fa-solid fa-sliders" />

                              Customization
                              available

                            </div>

                          )}

                       

                          <button
                            type="button"
                            className="food-add-btn food-menu-add-btn"
                            onClick={() =>
                              addFoodItem(
                                item,
                              )
                            }
                            disabled={
                              isAdding
                            }
                          >
                            {isAdding
                              ? "Adding..."
                              : "Add Item"}
                          </button>

                        </div>

                      </div>
                    );
                  },
                )}

              </div>

            )}

          </div>

        </div>

      </section>



      {modifierModalProduct && (

        <div className="booking-modal-overlay">

          <div className="booking-success-modal modifier-select-modal">

        

            <button
              type="button"
              className="booking-modal-close"
              onClick={
                closeModifierModal
              }
            >
              ×
            </button>

            <span className="booking-success-label">
              Customize Item
            </span>

            <h2>
              {
                modifierModalProduct.name
              }
            </h2>

            <p className="modifier-modal-desc">
              Select one modifier
              for this item.
            </p>

    

            <div className="modifier-option-list">

              {modifierModalProduct.modifiers.map(
                (
                  modifier,
                ) => {
                  const modifierId =
                    modifier._id ||
                    modifier.id;

                  const checked =
                    String(
                      selectedModifierId,
                    ) ===
                    String(
                      modifierId,
                    );

                  return (
                    <label
                      key={
                        modifierId
                      }
                      className={
                        checked
                          ? "modifier-option active"
                          : "modifier-option"
                      }
                    >

                      <input
                        type="radio"
                        name="foodModifier"
                        value={
                          modifierId
                        }
                        checked={
                          checked
                        }
                        onChange={() =>
                          setSelectedModifierId(
                            modifierId,
                          )
                        }
                      />

                      <span>
                        {modifier.modifierName ||
                          modifier.name ||
                          "Modifier"}
                      </span>

                      <strong>
                        +{" "}
                        {numberFormatter.format(
                          Number(
                            modifier.price ||
                              modifier.modifierPrice ||
                              0,
                          ),
                        )}
                      </strong>

                    </label>
                  );
                },
              )}

            </div>

     

            <div className="booking-total-modal">

              <span>
                Item Total
              </span>

              <strong>
                {numberFormatter.format(
                  Number(
                    modifierModalProduct.price ||
                      0,
                  ) +
                    Number(
                      modifierModalProduct.modifiers.find(
                        (
                          modifier,
                        ) =>
                          String(
                            modifier._id ||
                              modifier.id,
                          ) ===
                          String(
                            selectedModifierId,
                          ),
                      )?.price ||
                        modifierModalProduct.modifiers.find(
                          (
                            modifier,
                          ) =>
                            String(
                              modifier._id ||
                                modifier.id,
                            ) ===
                            String(
                              selectedModifierId,
                            ),
                        )
                          ?.modifierPrice ||
                        0,
                    ),
                )}
              </strong>

            </div>

      

            <button
              type="button"
              className="booking-done-btn"
              onClick={
                handleConfirmModifier
              }
              disabled={
                !selectedModifierId ||
                cartLoading
              }
            >
              {cartLoading
                ? "Adding..."
                : "Add Item"}
            </button>

          </div>

        </div>

      )}

      <Footer />

    </div>
  );
};

export default FoodMenu;